import React, { useState, useMemo } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { FaPlus } from "react-icons/fa";
import { FiEdit2, FiEye, FiPlay, FiCheck, FiCopy, FiSlash } from "react-icons/fi";
import PageLoader from "components/ui/PageLoader";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import SearchSelect from "components/ui/SearchSelect";
import Table from "components/ui/Table";
import Toggle from "components/ui/Toggle";
import Tooltip from "components/ui/Tooltip";
import { formatDate, formatDateTime } from "utils/helperFunctions";
import {
  useGetJurisdictionRulesQuery,
  useGetJurisdictionRuleOptionsQuery,
  useDraftJurisdictionRuleMutation,
  useEditJurisdictionRuleMutation,
  useSimulateJurisdictionRuleMutation,
  useApproveJurisdictionRuleMutation,
  useRetireJurisdictionRuleMutation,
} from "apis/jurisdictionRule";
import { useGetLegislationQuery } from "apis/legislation";
import { useGetJurisdictionsQuery } from "apis/division";

const CHECK_LABEL = {
  ratio: "Price range",
  allowedValues: "Allowed values",
  waitingPeriod: "Waiting period",
};

const STEP_LABEL = {
  "agreement.send": "Agency agreement",
  "goLive.update": "Advertised price",
  "signboard.update": "Signboard price",
  "brochure.update": "Brochure price",
  "mailcard.update": "Mailcard price",
  "offMarket.update": "Off Market price band",
  "buyerMatch.message": "Buyer match message",
};

const JUDGED_ON_LABEL = {
  today: "The day of the step",
  agreementDate: "The day the agency agreement was made",
};

const WAITING_PERIOD_LABEL = { "contract.coolingOff": "Contract of sale cooling off" };

const STATUS_STYLE = {
  draft: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  retired: "bg-lightgray text-darkgray",
};
const STATUS_LABEL = { draft: "Draft", approved: "Approved", retired: "Retired" };

// The kinds a price display is read as, for an allowedValues rule on priceDisplay.
const PRICE_DISPLAY_KINDS = "priced (shows a $ figure), underContract, unpriced";

const inputClass = (error) => `form-input border ${error ? "border-red-500" : "border-mediumgray"}`;
const labelOf = (labels, value) => labels[value] || value;
const optionsOf = (values, labels = {}) => values.map((value) => ({ value, label: labelOf(labels, value) }));
const pick = (options, value) => options.find((option) => option.value === value) || null;

// A stored date as the day it is where the rule applies ("2026-11-17"), for a date field.
const dayInput = (date, timezone) =>
  date ? new Intl.DateTimeFormat("en-CA", { timeZone: timezone || "UTC" }).format(new Date(date)) : "";

const ActionIcon = ({ label, disabled, onClick, children }) => (
  <Tooltip
    tooltip={label}
    text={
      <button
        className={`view-icon ${disabled ? "pointer-events-none opacity-40" : ""}`}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
      >
        {children}
      </button>
    }
  />
);

// What a rule says, in a line.
const valuesSummary = (rule) => {
  if (rule.isOpen) return "No answer yet: its steps are refused with its message";
  const { values } = rule;
  if (!values) return "—";
  if (rule.checkType === "ratio") {
    return `${values.high} at most ${Math.round((values.max - 1) * 10000) / 100}% above ${values.low}`;
  }
  if (rule.checkType === "allowedValues") return `${values.field}: ${values.allowed.join(", ")}`;
  if (rule.checkType === "waitingPeriod") {
    return `Ends at ${values.endsAt}, ${values.businessDays} business days after the day the clock starts (a record's own number wins)`;
  }
  return JSON.stringify(values);
};

const impactLine = (step, counts) =>
  `${labelOf({ ...STEP_LABEL, ...WAITING_PERIOD_LABEL }, step)}: ${counts.checked} checked, ${
    counts.refused ?? counts.changed
  } ${counts.refused != null ? "refused" : "would change"}`;

// ─── Draft / edit ─────────────────────────────────────────────────────────────
// A fresh one each time: the form writes into the object it starts from.
const emptyRule = () => ({
  jurisdiction: "",
  key: "",
  isOpen: false,
  checkType: "",
  ratio: { low: "startPrice", high: "endPrice", max: "1.1" },
  allowedValues: { field: "priceDisplay", allowed: "" },
  waitingPeriod: { businessDays: "5", endsAt: "17:00" },
  steps: [],
  when: "",
  judgedOn: "today",
  message: "",
  from: "",
  to: "",
  sources: [],
});

// A stored rule as the form holds it.
const formOf = (rule, timezone, asNewVersion) => ({
  ...emptyRule(),
  jurisdiction: rule.jurisdiction,
  key: rule.key,
  isOpen: !!rule.isOpen,
  checkType: rule.checkType || "",
  ...(rule.checkType === "ratio" && { ratio: { ...rule.values, max: String(rule.values.max) } }),
  ...(rule.checkType === "allowedValues" && {
    allowedValues: { field: rule.values.field, allowed: rule.values.allowed.join(", ") },
  }),
  ...(rule.checkType === "waitingPeriod" && {
    waitingPeriod: { businessDays: String(rule.values.businessDays), endsAt: rule.values.endsAt },
  }),
  steps: rule.steps || [],
  when: rule.when ? JSON.stringify(rule.when) : "",
  judgedOn: rule.judgedOn || "today",
  message: rule.message || "",
  from: asNewVersion ? "" : dayInput(rule.from, timezone),
  to: asNewVersion ? "" : dayInput(rule.to, timezone),
  sources: (rule.sources || []).map((source) => ({ ...source, document: String(source.document) })),
});

// The form as the backend takes it.
const ruleOf = (form) => {
  const valuesFor = {
    ratio: () => ({ low: form.ratio.low.trim(), high: form.ratio.high.trim(), max: Number(form.ratio.max) }),
    allowedValues: () => ({
      field: form.allowedValues.field.trim(),
      allowed: form.allowedValues.allowed
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean),
    }),
    waitingPeriod: () => ({
      businessDays: Number(form.waitingPeriod.businessDays),
      endsAt: form.waitingPeriod.endsAt,
    }),
  };
  const isWaitingPeriod = !form.isOpen && form.checkType === "waitingPeriod";
  return {
    jurisdiction: form.jurisdiction,
    key: form.key.trim(),
    isOpen: form.isOpen,
    checkType: form.isOpen ? null : form.checkType,
    values: form.isOpen ? null : valuesFor[form.checkType]?.() ?? null,
    steps: isWaitingPeriod ? [] : form.steps,
    when: isWaitingPeriod || !form.when.trim() ? null : JSON.parse(form.when),
    judgedOn: isWaitingPeriod ? "today" : form.judgedOn,
    message: form.message.trim() || null,
    from: form.from || null,
    to: form.to || null,
    sources: form.sources.map((source) => ({
      document: source.document,
      section: source.section.trim(),
      quote: source.quote.trim(),
    })),
  };
};

// Mounted only while open, so the form starts from its values instead of being reset.
const RuleModal = ({ editing, onClose, options, jurisdictions, laws }) => {
  const isEdit = !!editing.rule && !editing.asNewVersion;
  const draft = useDraftJurisdictionRuleMutation();
  const edit = useEditJurisdictionRuleMutation();
  const saving = draft.isPending || edit.isPending;
  const timezone = jurisdictions.find((jurisdiction) => jurisdiction.code === editing.rule?.jurisdiction)?.timezone;
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm({ defaultValues: editing.rule ? formOf(editing.rule, timezone, editing.asNewVersion) : emptyRule() });
  const { fields, append, remove } = useFieldArray({ control, name: "sources" });

  const isOpen = watch("isOpen");
  const checkType = watch("checkType");
  const isWaitingPeriod = !isOpen && checkType === "waitingPeriod";
  const jurisdictionOptions = jurisdictions.map((jurisdiction) => ({
    value: jurisdiction.code,
    label: `${jurisdiction.name} (${jurisdiction.code})`,
  }));
  const checkTypeOptions = optionsOf(options.checkTypes, CHECK_LABEL);
  const stepOptions = optionsOf(options.steps, STEP_LABEL);
  const judgedOnOptions = optionsOf(options.judgedOn, JUDGED_ON_LABEL);
  const waitingPeriodOptions = optionsOf(options.waitingPeriods, WAITING_PERIOD_LABEL);
  // Only a registered law can be quoted; the backend checks it suits the rule's jurisdiction on approval.
  const lawOptions = laws
    .filter((law) => law.registered && law.status === "ready")
    .map((law) => ({ value: law._id, label: `${law.title} · ${law.jurisdiction}${law.standing === "replaced" ? " (replaced)" : ""}` }));

  const submitForm = (form) => {
    const rule = ruleOf(form);
    const done = { onSuccess: onClose };
    if (isEdit) edit.mutate({ id: editing.rule._id, ...rule }, done);
    else draft.mutate(rule, done);
  };

  const title = isEdit ? "Edit Draft" : editing.asNewVersion ? "Draft a New Version" : "Draft a Rule";

  return (
    <Modal isOpen onClose={saving ? () => {} : onClose} title={title} className="max-w-3xl">
      <form onSubmit={handleSubmit(submitForm)} className="space-y-4">
        {editing.asNewVersion && (
          <p className="rounded bg-lightgray p-3 text-sm text-darkergray">
            Set the day the new version starts. Once it is approved, the version in force stops on that day.
          </p>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="form-label">Jurisdiction *</label>
            <Controller
              name="jurisdiction"
              control={control}
              rules={{ validate: (value) => !!value || "Choose a jurisdiction" }}
              render={({ field }) => (
                <SearchSelect
                  value={pick(jurisdictionOptions, field.value)}
                  onChange={(option) => field.onChange(option?.value || "")}
                  options={jurisdictionOptions}
                  disabled={isEdit || editing.asNewVersion}
                />
              )}
            />
            {errors.jurisdiction && <p className="form-error-message">{errors.jurisdiction.message}</p>}
          </div>
          <div className="space-y-2">
            <label className="form-label">No answer yet</label>
            <Controller
              name="isOpen"
              control={control}
              render={({ field }) => <Toggle checked={field.value} onChange={field.onChange} />}
            />
            <p className="text-xs text-darkgray">
              For a jurisdiction whose law isn't in the register: every step the rule runs at is refused with its
              message, so it is never given another jurisdiction's answer.
            </p>
          </div>
        </div>

        {!isOpen && (
          <div className="space-y-2">
            <label className="form-label">Kind of check *</label>
            <Controller
              name="checkType"
              control={control}
              rules={{ validate: (value) => isOpen || !!value || "Choose the kind of check" }}
              render={({ field }) => (
                <SearchSelect
                  value={pick(checkTypeOptions, field.value)}
                  onChange={(option) => field.onChange(option?.value || "")}
                  options={checkTypeOptions}
                  searchable={false}
                  disabled={isEdit || editing.asNewVersion}
                />
              )}
            />
            {errors.checkType && <p className="form-error-message">{errors.checkType.message}</p>}
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="key" className="form-label">
            {isWaitingPeriod ? "Deadline *" : "Key *"}
          </label>
          {isWaitingPeriod ? (
            <Controller
              name="key"
              control={control}
              rules={{ validate: (value) => !!value || "Choose the deadline" }}
              render={({ field }) => (
                <SearchSelect
                  value={pick(waitingPeriodOptions, field.value)}
                  onChange={(option) => field.onChange(option?.value || "")}
                  options={waitingPeriodOptions}
                  searchable={false}
                  disabled={isEdit || editing.asNewVersion}
                />
              )}
            />
          ) : (
            <input
              id="key"
              {...register("key", { validate: (value) => !!value.trim() || "A key is required" })}
              className={inputClass(errors.key)}
              placeholder="Dotted words naming what it governs, like price.estimateRange"
              disabled={isEdit || editing.asNewVersion}
            />
          )}
          {errors.key && <p className="form-error-message">{errors.key.message}</p>}
          <p className="text-xs text-darkgray">Versions of one rule share its jurisdiction and key.</p>
        </div>

        {!isOpen && checkType === "ratio" && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <label className="form-label">Lower figure</label>
              <input {...register("ratio.low")} className={inputClass()} />
            </div>
            <div className="space-y-2">
              <label className="form-label">Higher figure</label>
              <input {...register("ratio.high")} className={inputClass()} />
            </div>
            <div className="space-y-2">
              <label className="form-label">At most (times the lower)</label>
              <input type="number" step="0.001" {...register("ratio.max")} className={inputClass()} />
            </div>
          </div>
        )}

        {!isOpen && checkType === "allowedValues" && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="form-label">Input</label>
              <input {...register("allowedValues.field")} className={inputClass()} />
            </div>
            <div className="space-y-2">
              <label className="form-label">Allowed values</label>
              <input {...register("allowedValues.allowed")} className={inputClass()} placeholder="Separated by commas" />
              <p className="text-xs text-darkgray">A price display is read as {PRICE_DISPLAY_KINDS}.</p>
            </div>
          </div>
        )}

        {isWaitingPeriod && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="form-label">Business days</label>
              <input type="number" min="0" max="60" {...register("waitingPeriod.businessDays")} className={inputClass()} />
              <p className="text-xs text-darkgray">After the day the clock starts. A contract's own number wins.</p>
            </div>
            <div className="space-y-2">
              <label className="form-label">Ends at (local time)</label>
              <input type="time" {...register("waitingPeriod.endsAt")} className={inputClass()} />
              <p className="text-xs text-darkgray">Business days skip weekends and public and bank holidays there.</p>
            </div>
          </div>
        )}

        {!isWaitingPeriod && (
          <div className="space-y-2">
            <label className="form-label">Where it runs *</label>
            <Controller
              name="steps"
              control={control}
              rules={{ validate: (value) => isWaitingPeriod || value.length > 0 || "Choose at least one step" }}
              render={({ field }) => (
                <SearchSelect
                  multiple
                  value={stepOptions.filter((option) => field.value.includes(option.value))}
                  onChange={(chosen) => field.onChange((chosen || []).map((option) => option.value))}
                  options={stepOptions}
                  placeholder="Steps that ask this rule"
                />
              )}
            />
            {errors.steps && <p className="form-error-message">{errors.steps.message}</p>}
          </div>
        )}

        {!isWaitingPeriod && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="form-label">Only for listings</label>
              <input
                {...register("when", {
                  validate: (value) => {
                    if (!value.trim()) return true;
                    try {
                      JSON.parse(value);
                      return true;
                    } catch {
                      return 'Write it like {"saleProcess": ["Auction"]}';
                    }
                  },
                })}
                className={inputClass(errors.when)}
                placeholder='Empty for every listing, or {"saleProcess": ["Auction"]}'
              />
              {errors.when && <p className="form-error-message">{errors.when.message}</p>}
            </div>
            <div className="space-y-2">
              <label className="form-label">Judged on</label>
              <Controller
                name="judgedOn"
                control={control}
                render={({ field }) => (
                  <SearchSelect
                    value={pick(judgedOnOptions, field.value)}
                    onChange={(option) => field.onChange(option?.value || "today")}
                    options={judgedOnOptions}
                    searchable={false}
                  />
                )}
              />
            </div>
          </div>
        )}

        {!isWaitingPeriod && (
          <div className="space-y-2">
            <label htmlFor="message" className="form-label">
              Message {isOpen && "*"}
            </label>
            <textarea
              id="message"
              rows={2}
              {...register("message", { validate: (value) => !isOpen || !!value.trim() || "An open rule needs its message" })}
              className={inputClass(errors.message)}
              placeholder="What the agent sees when a step is refused, and what to change"
            />
            {errors.message && <p className="form-error-message">{errors.message.message}</p>}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="from" className="form-label">
              In force from
            </label>
            <input id="from" type="date" {...register("from")} className={inputClass()} />
            <p className="text-xs text-darkgray">Empty = always has been. Midnight where the rule applies.</p>
          </div>
          <div className="space-y-2">
            <label htmlFor="to" className="form-label">
              Until
            </label>
            <input id="to" type="date" {...register("to")} className={inputClass()} />
            <p className="text-xs text-darkgray">Usually empty: approving a later version sets it.</p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="form-label">The law it comes from</label>
            <Button className="outline-button" onClick={() => append({ document: "", section: "", quote: "" })}>
              Add a quote
            </Button>
          </div>
          <p className="text-xs text-darkgray">
            Quote the Act or Regulation word for word from the register; guidance can be quoted with it, never alone. An
            open rule needs none.
          </p>
          {fields.map((field, index) => (
            <div key={field.id} className="space-y-2 rounded border border-mediumgray p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                <div className="md:col-span-2">
                  <Controller
                    name={`sources.${index}.document`}
                    control={control}
                    rules={{ validate: (value) => !!value || "Choose the law" }}
                    render={({ field: documentField }) => (
                      <SearchSelect
                        value={pick(lawOptions, documentField.value)}
                        onChange={(option) => documentField.onChange(option?.value || "")}
                        options={lawOptions}
                        placeholder="A registered law"
                      />
                    )}
                  />
                  {errors.sources?.[index]?.document && (
                    <p className="form-error-message">{errors.sources[index].document.message}</p>
                  )}
                </div>
                <input
                  {...register(`sources.${index}.section`, { validate: (value) => !!value.trim() || "The section" })}
                  className={inputClass(errors.sources?.[index]?.section)}
                  placeholder="Section, e.g. s 66S(3)"
                />
              </div>
              <textarea
                rows={3}
                {...register(`sources.${index}.quote`, { validate: (value) => !!value.trim() || "The section's words" })}
                className={inputClass(errors.sources?.[index]?.quote)}
                placeholder="Its exact words, copied from the stored text"
              />
              <div className="flex justify-end">
                <Button className="outline-button" onClick={() => remove(index)}>
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-mediumgray">
          <Button className="outline-button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" className="black-button" loading={saving}>
            Save Draft
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ─── Everything about one rule ────────────────────────────────────────────────
const RuleDetailsModal = ({ rule, laws, onClose }) => {
  const titleOf = new Map(laws.map((law) => [String(law._id), law.title]));
  return (
    <Modal isOpen={!!rule} onClose={onClose} title={rule ? `${rule.jurisdiction} · ${rule.key}` : ""} className="max-w-3xl">
      {rule && (
        <div className="space-y-4 text-sm text-darkergray">
          <div>
            <h5 className="mb-1">What it says</h5>
            <p>{valuesSummary(rule)}</p>
            {rule.message && <p className="mt-1 text-darkgray">Message: {rule.message}</p>}
            {rule.when && <p className="mt-1 text-darkgray">Only for listings: {JSON.stringify(rule.when)}</p>}
            <p className="mt-1 text-darkgray">
              {rule.steps?.length
                ? `Runs at: ${rule.steps.map((step) => labelOf(STEP_LABEL, step)).join(", ")}`
                : "Runs at no step: read wherever its deadline is worked out"}
              {rule.steps?.length > 0 && ` · judged on ${labelOf(JUDGED_ON_LABEL, rule.judgedOn).toLowerCase()}`}
            </p>
            <p className="mt-1 text-darkgray">
              In force {rule.from ? `from ${formatDate(rule.from)}` : "always"}
              {rule.to && ` until ${formatDate(rule.to)}`}
            </p>
          </div>

          {rule.recheck?.length > 0 && (
            <div className="rounded bg-red-50 p-3">
              <h5 className="mb-1">Needs checking against a new version</h5>
              {rule.recheck.map((item) => (
                <p key={item.document}>
                  {item.title} is replaced by {item.replacedByTitle}
                  {item.on ? ` from ${formatDate(item.on)}` : ""}. Read the rule against it, then draft a new version
                  quoting it.
                </p>
              ))}
            </div>
          )}

          <div>
            <h5 className="mb-1">The law it comes from</h5>
            {rule.sources?.length ? (
              rule.sources.map((source, index) => (
                <div key={index} className="mb-2 rounded bg-lightgray p-3">
                  <p className="text-xs text-darkgray">
                    {titleOf.get(String(source.document)) || "A law no longer listed"} · {source.section}
                  </p>
                  <p className="whitespace-pre-wrap">{source.quote}</p>
                </div>
              ))
            ) : (
              <p className="text-darkgray">None quoted.</p>
            )}
          </div>

          <div>
            <h5 className="mb-1">Last simulation</h5>
            {rule.impact ? (
              <>
                <p className="text-darkgray">
                  {formatDateTime(rule.impact.ranAt)}, as at {formatDate(rule.impact.at)}
                </p>
                {Object.entries(rule.impact.steps || {}).map(([step, counts]) => (
                  <p key={step}>{impactLine(step, counts)}</p>
                ))}
                {rule.impact.sample?.map((row, index) => (
                  <p key={index} className="text-xs text-darkgray">
                    {row.address || row.propertyId}: {row.message}
                  </p>
                ))}
              </>
            ) : (
              <p className="text-darkgray">Not simulated since its last edit. Simulate it before approving.</p>
            )}
          </div>

          <div>
            <h5 className="mb-1">History</h5>
            {(rule.history || []).map((entry, index) => (
              <p key={index} className="text-xs text-darkgray">
                {formatDateTime(entry.at)} · {entry.action}
              </p>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

// A step in a rule's life that can't be undone with one click, confirmed first.
const ConfirmModal = ({ confirming, onClose }) => {
  const approve = useApproveJurisdictionRuleMutation();
  const retire = useRetireJurisdictionRuleMutation();
  const mutation = confirming?.action === "approve" ? approve : retire;
  const rule = confirming?.rule;
  return (
    <Modal
      isOpen={!!confirming}
      onClose={onClose}
      title={confirming?.action === "approve" ? "Approve Rule" : "Retire Rule"}
      className="max-w-md"
    >
      <p className="text-sm text-darkergray">
        {confirming?.action === "approve" ? (
          <>
            Approve <strong>{rule?.jurisdiction} · {rule?.key}</strong>? It applies to every agent there
            {rule?.from ? ` from ${formatDate(rule.from)}` : " straight away"}, within a minute, and the version it
            replaces stops then. Its quotes are checked against the stored law first.
          </>
        ) : (
          <>
            Retire <strong>{rule?.jurisdiction} · {rule?.key}</strong>? It stops applying within a minute, and the
            version it replaced runs on again.
          </>
        )}
      </p>
      <div className="flex justify-end gap-2 pt-4">
        <Button className="outline-button" onClick={onClose}>
          Cancel
        </Button>
        <Button className="black-button" loading={mutation.isPending} onClick={() => mutation.mutate(rule._id, { onSuccess: onClose })}>
          {confirming?.action === "approve" ? "Approve" : "Retire"}
        </Button>
      </div>
    </Modal>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const pageSize = 20;

const JurisdictionRules = () => {
  const [editing, setEditing] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [recheckOnly, setRecheckOnly] = useState(false);

  const { data, isPending } = useGetJurisdictionRulesQuery();
  const { data: options } = useGetJurisdictionRuleOptionsQuery();
  const { data: laws } = useGetLegislationQuery();
  const { data: jurisdictions } = useGetJurisdictionsQuery();
  const simulate = useSimulateJurisdictionRuleMutation();

  const shown = recheckOnly ? data.filter((rule) => rule.recheck?.length) : data;
  const viewing = data.find((rule) => rule._id === viewingId) || null;

  const columns = useMemo(
    () => [
      {
        accessorKey: "key",
        header: "Rule",
        isFilter: true,
        minWidth: "220px",
        cell: (row) => (
          <div>
            <p className="font-medium text-darkergray">{row.key}</p>
            <p className="text-xs text-darkgray">{row.jurisdiction}</p>
          </div>
        ),
      },
      {
        accessorKey: "checkType",
        header: "Kind",
        isFilter: true,
        cell: (row) => (row.isOpen ? "Open" : labelOf(CHECK_LABEL, row.checkType)),
      },
      {
        accessorKey: "values",
        header: "What it says",
        isFilter: false,
        isSortable: false,
        minWidth: "260px",
        cell: (row) => <p className="text-xs">{valuesSummary(row)}</p>,
      },
      {
        accessorKey: "from",
        header: "In force",
        isFilter: false,
        minWidth: "150px",
        cell: (row) => (
          <p className="text-xs">
            {row.from ? `From ${formatDate(row.from)}` : "Always"}
            {row.to && <span className="block">Until {formatDate(row.to)}</span>}
          </p>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        isFilter: true,
        cell: (row) => (
          <div className="space-y-1">
            <span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[row.status] || ""}`}>
              {STATUS_LABEL[row.status] || row.status}
            </span>
            {row.recheck?.length > 0 && (
              <span className="block w-fit rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">Check again</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "impact",
        header: "Simulated",
        isFilter: false,
        isSortable: false,
        cell: (row) =>
          row.impact ? (
            <p className="text-xs">
              {row.impact.failing} {row.checkType === "waitingPeriod" ? "would change" : "refused"}
              <span className="block text-darkgray">{formatDateTime(row.impact.ranAt)}</span>
            </p>
          ) : (
            <span className="text-xs text-darkgray">Not yet</span>
          ),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        cell: (row) => (
          <div className="flex items-center gap-3">
            <ActionIcon label="Everything about it" onClick={() => setViewingId(row._id)}>
              <FiEye size={16} />
            </ActionIcon>
            {row.status === "draft" && (
              <ActionIcon label="Edit the draft" onClick={() => setEditing({ rule: row })}>
                <FiEdit2 size={16} />
              </ActionIcon>
            )}
            {row.status !== "retired" && (
              <ActionIcon label="Simulate on live records" disabled={simulate.isPending} onClick={() => simulate.mutate(row._id)}>
                <FiPlay size={16} />
              </ActionIcon>
            )}
            {row.status === "draft" && (
              <ActionIcon
                label={row.impact ? "Approve" : "Simulate it first"}
                disabled={!row.impact}
                onClick={() => setConfirming({ action: "approve", rule: row })}
              >
                <FiCheck size={16} />
              </ActionIcon>
            )}
            {row.status === "approved" && (
              <ActionIcon label="Draft a new version" onClick={() => setEditing({ rule: row, asNewVersion: true })}>
                <FiCopy size={16} />
              </ActionIcon>
            )}
            {row.status !== "retired" && (
              <ActionIcon label="Retire" onClick={() => setConfirming({ action: "retire", rule: row })}>
                <FiSlash size={16} />
              </ActionIcon>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, simulate.isPending],
  );

  const DraftButton = () => (
    <div className="flex items-center gap-4">
      <label className="flex items-center gap-2 text-sm text-darkergray">
        <Toggle checked={recheckOnly} onChange={setRecheckOnly} />
        Needs checking only
      </label>
      <Button className="black-button flex items-center gap-2" onClick={() => setEditing({})}>
        <FaPlus size={14} />
        Draft a Rule
      </Button>
    </div>
  );

  if (isPending) return <PageLoader />;

  return (
    <>
      {editing && (
        <RuleModal editing={editing} onClose={() => setEditing(null)} options={options} jurisdictions={jurisdictions} laws={laws} />
      )}
      <RuleDetailsModal rule={viewing} laws={laws} onClose={() => setViewingId(null)} />
      <ConfirmModal confirming={confirming} onClose={() => setConfirming(null)} />

      <div className="container mx-auto">
        <p className="mb-4 text-sm text-darkgray">
          The law each state or country applies, held as records: price ranges, price displays, cooling off and more.
          A rule applies only once simulated and approved by a super admin, and quotes the stored law it comes from. A
          change is a new version from its start date; an approved rule is never edited.
        </p>
        <Table
          title="Jurisdiction Rules"
          columns={columns}
          data={shown}
          pageSize={pageSize}
          ButtonCmp={DraftButton}
          emptyText={recheckOnly ? "No rule needs checking against a new law." : "No rules yet."}
        />
      </div>
    </>
  );
};

export default JurisdictionRules;
